import React, { createContext, useCallback, useContext, useMemo, useState } from "react";

export interface ImageSettings {
  brightness: number;
  contrast: number;
  rotation: number;
  flipX: boolean;
  flipY: boolean;
}

export interface ImageSettingsContextType {
  imageSettings: ImageSettings;
  // update setting accepts values that are defined for each key on ImageSettings
  updateSetting: <K extends keyof ImageSettings>(key: K, value: ImageSettings[K]) => void;
  // toggleSetting accepts only keys where the value is a boolean
  toggleSetting: <K extends keyof ImageSettings>(
    key: K extends keyof ImageSettings ? (ImageSettings[K] extends boolean ? K : never) : never
  ) => void;
  resetImageSettings: () => void;
  zoom: number;
  setZoom: (zoom: number) => void;
  isEditing: boolean;
  setIsEditing: (isEditing: boolean) => void;
  isOnReview: boolean;
  setIsOnReview: (isOnReview: boolean) => void;
}

const initialImageSettings = {
  brightness: 100,
  contrast: 100,
  rotation: 0,
  flipX: false,
  flipY: false,
};

export const ImageSettingsContext = createContext<ImageSettingsContextType | null>(null);

type ImageSettingsProviderProps = React.PropsWithChildren<{
  isEditing?: boolean;
  isOnReview?: boolean;
}>;

export function ImageSettingsProvider({
  children,
  isEditing: defaultIsEditing = false,
  isOnReview: defaultIsOnReview = false
}: ImageSettingsProviderProps) {
  const [imageSettings, setImageSettings] = useState<ImageSettings>(initialImageSettings);

  const updateSetting: ImageSettingsContextType["updateSetting"] = useCallback(
    (key, value) => setImageSettings((prev) => ({ ...prev, [key]: value })),
    [setImageSettings]
  );

  const toggleSetting: ImageSettingsContextType["toggleSetting"] = useCallback(
    (key) => setImageSettings((prev) => ({ ...prev, [key]: !prev[key] })),
    [setImageSettings]
  );

  const resetImageSettings = useCallback(() => {
    setImageSettings(initialImageSettings);
  }, []);

  const [zoom, setZoom] = useState(1);
  const [isOnReview, setIsOnReview] = useState(defaultIsOnReview);
  const [isEditing, setIsEditing] = useState(defaultIsEditing);

  const memorizeValues: ImageSettingsContextType = useMemo(
    () => ({
      imageSettings,
      updateSetting,
      toggleSetting,
      resetImageSettings,
      zoom,
      setZoom,
      isEditing,
      setIsEditing,
      isOnReview,
      setIsOnReview,
    }),
    [
      imageSettings,
      updateSetting,
      toggleSetting,
      resetImageSettings,
      zoom,
      isEditing,
      setIsEditing,
      isOnReview,
      setIsOnReview
    ]
  );

  return <ImageSettingsContext.Provider value={memorizeValues}>{children}</ImageSettingsContext.Provider>;
}

export function useImageSettingsContext() {
  const context = useContext(ImageSettingsContext);

  if (!context) {
    throw new Error("useImageSettingsContext must be used inside of ImageSettingsProvider");
  }

  return context;
}
